import React, { useState } from "react";
import api from "../../api/api";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    InputAdornment,
} from "@mui/material";

import {
    Email,
    Engineering,
    ArrowBack,
    ArrowForward,
    VerifiedUser,
    Build,
    Construction,
    ElectricalServices,
    Handyman,
    LockReset,
} from "@mui/icons-material";

const COLORS = {
    navy: "#071923",
    deepNavy: "#041017",
    charcoal: "#0D2028",
    steel: "#18343F",
    green: "#16834B",
    brightGreen: "#22A861",
    gold: "#E5A91A",
    brightGold: "#F4C542",
    white: "#FFFFFF",
    cream: "#F7F8F5",
    text: "#12252D",
    muted: "#6B7A80",
    border: "#DCE5E5",
    paleGreen: "#EEF8F2",
    red: "#B42318",
};

const FloatingTradeIcon = ({
    Icon,
    top,
    left,
    right,
    bottom,
    delay = 0,
    size = 28,
}) => (
    <motion.div
        initial={{
            opacity: 0,
            y: 20,
            scale: 0.7,
        }}
        animate={{
            opacity: [0, 0.7, 0.4, 0],
            y: [20, -5, -30, -60],
            x: [0, 10, -5, 15],
            rotate: [0, 10, -8, 15],
            scale: [0.7, 1, 1.05, 0.65],
        }}
        transition={{
            duration: 8,
            delay,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            top,
            left,
            right,
            bottom,
            zIndex: 3,
            pointerEvents: "none",
        }}
    >
        <Icon
            sx={{
                fontSize: size,
                color: COLORS.brightGold,
                opacity: 0.7,
                filter:
                    "drop-shadow(0 0 10px rgba(244,197,66,.25))",
            }}
        />
    </motion.div>
);

const Glow = ({
    size = 400,
    top,
    left,
    right,
    bottom,
    color = "rgba(34,168,97,.16)",
}) => (
    <motion.div
        animate={{
            scale: [0.92, 1.08, 0.92],
            opacity: [0.35, 0.6, 0.35],
        }}
        transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            width: size,
            height: size,
            top,
            left,
            right,
            bottom,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
            pointerEvents: "none",
            zIndex: 1,
        }}
    />
);

const TechnicalGrid = () => (
    <Box
        sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.07,
            pointerEvents: "none",
            backgroundImage: `
                linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)
            `,
            backgroundSize: "42px 42px",
            maskImage:
                "linear-gradient(to bottom, black, transparent 90%)",
        }}
    />
);

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const handleRequest = async (e) => {
        e.preventDefault();

        const cleanEmail = email.trim();

        if (!cleanEmail) {
            setMessage(
                "Please enter the email address associated with your account."
            );
            return;
        }

        setLoading(true);
        setMessage("");

        setTimeout(async () => {
            try {
                const res = await api.post(
                    "/request-reset-code/",
                    {
                        email: cleanEmail,
                    }
                );

                setMessage(
                    res.data.message ||
                        "Verification code sent."
                );

                navigate("/verifyCode", {
                    state: {
                        email: cleanEmail,
                    },
                });
            } catch (err) {
                setMessage(
                    err.response?.data?.error ||
                        err.response?.data?.message ||
                        "Something went wrong. Please try again."
                );
            } finally {
                setLoading(false);
            }
        }, 1200);
    };

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                width: "100%",
                background: COLORS.cream,
                overflow: "hidden",
                position: "relative",
            }}
        >
            <Box
                sx={{
                    minHeight: "100dvh",
                    display: {
                        xs: "block",
                        md: "grid",
                    },
                    gridTemplateColumns: {
                        md: "1.08fr .92fr",
                    },
                }}
            >
                {/* =========================================================
                    LEFT — TRADE TRAINING BRAND AREA
                ========================================================= */}

                <Box
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex",
                        },
                        position: "relative",
                        minHeight: "100dvh",
                        overflow: "hidden",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        px: {
                            md: 6,
                            lg: 8,
                            xl: 10,
                        },
                        py: 5,
                        color: COLORS.white,
                        background:
                            "linear-gradient(135deg, #041017 0%, #071923 48%, #0C3027 100%)",
                    }}
                >
                    <TechnicalGrid />

                    <Glow
                        size={650}
                        top={-350}
                        left={-300}
                        color="rgba(34,168,97,.20)"
                    />

                    <Glow
                        size={500}
                        bottom={-300}
                        right={-250}
                        color="rgba(229,169,26,.12)"
                    />

                    <FloatingTradeIcon
                        Icon={Engineering}
                        top="19%"
                        left="9%"
                        delay={0}
                        size={31}
                    />

                    <FloatingTradeIcon
                        Icon={Construction}
                        top="33%"
                        right="11%"
                        delay={1}
                        size={29}
                    />

                    <FloatingTradeIcon
                        Icon={ElectricalServices}
                        top="49%"
                        left="7%"
                        delay={2}
                        size={30}
                    />

                    <FloatingTradeIcon
                        Icon={Handyman}
                        top="66%"
                        right="10%"
                        delay={3}
                        size={29}
                    />

                    <FloatingTradeIcon
                        Icon={Build}
                        top="77%"
                        left="20%"
                        delay={4}
                        size={28}
                    />

                    {/* HEADER */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "13px",
                                background:
                                    "linear-gradient(135deg, #16834B, #22A861)",
                                boxShadow:
                                    "0 12px 30px rgba(0,0,0,.3)",
                            }}
                        >
                            <Engineering
                                sx={{
                                    fontSize: 28,
                                    color: COLORS.white,
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    fontSize: ".7rem",
                                    fontWeight: 950,
                                    letterSpacing: 1.8,
                                    color:
                                        COLORS.brightGold,
                                }}
                            >
                                POLU
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: ".82rem",
                                    fontWeight: 800,
                                    letterSpacing: 1,
                                    color:
                                        "rgba(255,255,255,.82)",
                                }}
                            >
                                TRAINING MANAGEMENT SYSTEM
                            </Typography>
                        </Box>
                    </Box>

                    {/* CENTER */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            maxWidth: 680,
                            mt: -5,
                        }}
                    >
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: .8,
                                px: 1.7,
                                py: .8,
                                mb: 2.5,
                                borderRadius: "6px",
                                background:
                                    "rgba(229,169,26,.12)",
                                border:
                                    "1px solid rgba(229,169,26,.28)",
                            }}
                        >
                            <LockReset
                                sx={{
                                    fontSize: 16,
                                    color:
                                        COLORS.brightGold,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: ".62rem",
                                    fontWeight: 950,
                                    letterSpacing: 1.7,
                                    color:
                                        COLORS.brightGold,
                                }}
                            >
                                ACCOUNT RECOVERY
                            </Typography>
                        </Box>

                        <Typography
                            sx={{
                                fontSize: {
                                    md: "3.4rem",
                                    lg: "4.4rem",
                                    xl: "5rem",
                                },
                                fontWeight: 1000,
                                lineHeight: .96,
                                letterSpacing: "-2.8px",
                                textTransform:
                                    "uppercase",
                            }}
                        >
                            Get Back
                            <br />
                            To Your
                            <Box
                                component="span"
                                sx={{
                                    display: "block",
                                    color:
                                        COLORS.brightGold,
                                }}
                            >
                                Training.
                            </Box>
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 560,
                                mt: 3,
                                color:
                                    "rgba(255,255,255,.66)",
                                fontSize: "1rem",
                                lineHeight: 1.8,
                            }}
                        >
                            Forgot your password? No problem.
                            Enter the email connected to your
                            training account and we'll help you
                            securely restore access.
                        </Typography>

                        {/* RECOVERY STEPS */}

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(3, 1fr)",
                                gap: 1.2,
                                maxWidth: 600,
                                mt: 4,
                            }}
                        >
                            {[
                                {
                                    number: "01",
                                    title: "EMAIL",
                                },
                                {
                                    number: "02",
                                    title: "VERIFY",
                                },
                                {
                                    number: "03",
                                    title: "RESET",
                                },
                            ].map((item) => (
                                <Box
                                    key={item.number}
                                    sx={{
                                        px: 1.5,
                                        py: 1.5,
                                        borderRadius:
                                            "10px",
                                        background:
                                            "rgba(255,255,255,.045)",
                                        border:
                                            "1px solid rgba(255,255,255,.08)",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize:
                                                ".6rem",
                                            fontWeight:
                                                950,
                                            color:
                                                COLORS.brightGold,
                                            letterSpacing:
                                                1,
                                        }}
                                    >
                                        {item.number}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: .4,
                                            fontSize:
                                                ".72rem",
                                            fontWeight:
                                                900,
                                            color:
                                                "rgba(255,255,255,.78)",
                                        }}
                                    >
                                        {item.title}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* FOOTER */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            borderTop:
                                "1px solid rgba(255,255,255,.10)",
                            pt: 2.5,
                            display: "flex",
                            justifyContent:
                                "space-between",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: ".63rem",
                                fontWeight: 800,
                                letterSpacing: 1.3,
                                color:
                                    "rgba(255,255,255,.43)",
                            }}
                        >
                            SKILLS • INNOVATION • EXCELLENCE
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: ".63rem",
                                fontWeight: 800,
                                color:
                                    "rgba(255,255,255,.43)",
                            }}
                        >
                            SKILLS FOR ALL
                        </Typography>
                    </Box>
                </Box>

                {/* =========================================================
                    RIGHT — RECOVERY FORM
                ========================================================= */}

                <Box
                    sx={{
                        minHeight: "100dvh",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        px: {
                            xs: 2,
                            sm: 4,
                            md: 5,
                            lg: 7,
                        },
                        py: {
                            xs: 6,
                            md: 5,
                        },
                        background:
                            "linear-gradient(150deg, #FFFFFF 0%, #F6FAF8 100%)",
                    }}
                >
                    <Glow
                        size={400}
                        top={-230}
                        right={-220}
                        color="rgba(34,168,97,.10)"
                    />

                    <Glow
                        size={330}
                        bottom={-200}
                        left={-190}
                        color="rgba(229,169,26,.08)"
                    />

                    {/* MOBILE BRAND */}

                    <Box
                        sx={{
                            display: {
                                xs: "flex",
                                md: "none",
                            },
                            position: "absolute",
                            top: 18,
                            left: 18,
                            right: 18,
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems:
                                    "center",
                                gap: .9,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    borderRadius:
                                        "10px",
                                    background:
                                        COLORS.navy,
                                }}
                            >
                                <Engineering
                                    sx={{
                                        color:
                                            COLORS.brightGold,
                                        fontSize: 21,
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: ".68rem",
                                    fontWeight: 950,
                                    letterSpacing: 1,
                                    color:
                                        COLORS.navy,
                                }}
                            >
                                TRADE TRAINING
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                px: 1.2,
                                py: .55,
                                borderRadius:
                                    "20px",
                                background:
                                    COLORS.paleGreen,
                                color:
                                    COLORS.green,
                                fontSize:
                                    ".58rem",
                                fontWeight: 900,
                            }}
                        >
                            SECURE
                        </Box>
                    </Box>

                    {/* FORM AREA */}

                    <motion.div
                        initial={{
                            opacity: 0,
                            x: 35,
                        }}
                        animate={{
                            opacity: 1,
                            x: 0,
                        }}
                        transition={{
                            duration: .7,
                            ease: "easeOut",
                        }}
                        style={{
                            position: "relative",
                            zIndex: 10,
                            width: "100%",
                            maxWidth: 455,
                        }}
                    >
                        {/* ICON */}

                        <Box
                            sx={{
                                textAlign: "center",
                                mb: 3,
                            }}
                        >
                            <motion.div
                                animate={{
                                    y: [0, -7, 0],
                                    rotate: [
                                        0,
                                        2,
                                        -2,
                                        0,
                                    ],
                                }}
                                transition={{
                                    duration: 3.5,
                                    repeat:
                                        Infinity,
                                    ease:
                                        "easeInOut",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 70,
                                        height: 70,
                                        mx: "auto",
                                        mb: 2,
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                        borderRadius:
                                            "20px",
                                        background:
                                            "linear-gradient(135deg, #071923, #16834B)",
                                        boxShadow:
                                            "0 18px 35px rgba(7,25,35,.18)",
                                        position:
                                            "relative",
                                    }}
                                >
                                    <LockReset
                                        sx={{
                                            fontSize: 36,
                                            color:
                                                COLORS.white,
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            position:
                                                "absolute",
                                            width: 13,
                                            height: 13,
                                            right: -4,
                                            top: -4,
                                            borderRadius:
                                                "50%",
                                            background:
                                                COLORS.brightGold,
                                            border:
                                                "3px solid #F7F8F5",
                                        }}
                                    />
                                </Box>
                            </motion.div>

                            <Typography
                                sx={{
                                    fontSize: ".67rem",
                                    fontWeight: 950,
                                    letterSpacing: 2,
                                    color:
                                        COLORS.green,
                                }}
                            >
                                TRADE TRAINING INSTITUTE
                            </Typography>

                            <Typography
                                sx={{
                                    mt: .7,
                                    fontSize: {
                                        xs: "1.9rem",
                                        sm: "2.25rem",
                                    },
                                    fontWeight: 1000,
                                    letterSpacing:
                                        "-1px",
                                    color:
                                        COLORS.navy,
                                    lineHeight: 1.05,
                                }}
                            >
                                Forgot Password?
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 1,
                                    color:
                                        COLORS.muted,
                                    fontSize:
                                        ".86rem",
                                    lineHeight:
                                        1.65,
                                    maxWidth:
                                        390,
                                    mx: "auto",
                                }}
                            >
                                Enter your account email
                                to receive a secure
                                verification code.
                            </Typography>
                        </Box>

                        {/* FORM CARD */}

                        <Box
                            sx={{
                                position:
                                    "relative",
                                p: {
                                    xs: 2.3,
                                    sm: 3.5,
                                },
                                borderRadius:
                                    "24px",
                                background:
                                    "rgba(255,255,255,.94)",
                                border:
                                    `1px solid ${COLORS.border}`,
                                boxShadow:
                                    "0 25px 70px rgba(7,25,35,.10)",
                                backdropFilter:
                                    "blur(18px)",
                            }}
                        >
                            {/* SECURITY HEADER */}

                            <Box
                                sx={{
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    gap: 1.2,
                                    mb: 2.5,
                                    pb: 2,
                                    borderBottom:
                                        `1px solid ${COLORS.border}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius:
                                            "10px",
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                        background:
                                            COLORS.paleGreen,
                                    }}
                                >
                                    <VerifiedUser
                                        sx={{
                                            fontSize:
                                                20,
                                            color:
                                                COLORS.green,
                                        }}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize:
                                                ".76rem",
                                            fontWeight:
                                                950,
                                            color:
                                                COLORS.navy,
                                        }}
                                    >
                                        SECURE ACCOUNT RECOVERY
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt:
                                                .25,
                                            fontSize:
                                                ".64rem",
                                            color:
                                                COLORS.muted,
                                        }}
                                    >
                                        Verification code required
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                component="form"
                                onSubmit={
                                    handleRequest
                                }
                            >
                                <Typography
                                    sx={{
                                        mb:
                                            .75,
                                        fontSize:
                                            ".68rem",
                                        fontWeight:
                                            900,
                                        color:
                                            COLORS.text,
                                    }}
                                >
                                    EMAIL ADDRESS
                                </Typography>

                                <TextField
                                    fullWidth
                                    required
                                    type="email"
                                    placeholder="Enter your email address"
                                    value={email}
                                    disabled={
                                        loading
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setEmail(
                                            e.target
                                                .value
                                        )
                                    }
                                    autoComplete="email"
                                    InputProps={{
                                        startAdornment:
                                            (
                                                <InputAdornment position="start">
                                                    <Email
                                                        sx={{
                                                            fontSize:
                                                                20,
                                                            color:
                                                                COLORS.green,
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                    }}
                                    sx={{
                                        mb: 2.5,
                                        "& .MuiOutlinedInput-root":
                                            {
                                                minHeight:
                                                    56,
                                                borderRadius:
                                                    "12px",
                                                background:
                                                    "#FBFDFC",
                                                "& fieldset":
                                                    {
                                                        borderColor:
                                                            COLORS.border,
                                                    },
                                                "&:hover fieldset":
                                                    {
                                                        borderColor:
                                                            "#9CCDB2",
                                                    },
                                                "&.Mui-focused fieldset":
                                                    {
                                                        borderColor:
                                                            COLORS.green,
                                                        borderWidth:
                                                            1.5,
                                                    },
                                            },
                                        "& input":
                                            {
                                                fontSize:
                                                    ".88rem",
                                            },
                                    }}
                                />

                                <Button
                                    fullWidth
                                    type="submit"
                                    disabled={
                                        loading
                                    }
                                    sx={{
                                        minHeight:
                                            57,
                                        borderRadius:
                                            "12px",
                                        textTransform:
                                            "none",
                                        fontSize:
                                            ".9rem",
                                        fontWeight:
                                            950,
                                        color:
                                            COLORS.white,
                                        background:
                                            `linear-gradient(
                                                135deg,
                                                ${COLORS.navy},
                                                ${COLORS.steel},
                                                ${COLORS.green}
                                            )`,
                                        boxShadow:
                                            "0 14px 28px rgba(7,25,35,.17)",
                                        transition:
                                            "all .25s ease",
                                        "&:hover":
                                            {
                                                background:
                                                    `linear-gradient(
                                                        135deg,
                                                        ${COLORS.steel},
                                                        ${COLORS.green}
                                                    )`,
                                                transform:
                                                    "translateY(-1px)",
                                                boxShadow:
                                                    "0 18px 34px rgba(7,25,35,.22)",
                                            },
                                        "&:disabled":
                                            {
                                                color:
                                                    COLORS.white,
                                                background:
                                                    "#93A39A",
                                            },
                                    }}
                                >
                                    {loading ? (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                gap:
                                                    1.2,
                                            }}
                                        >
                                            <CircularProgress
                                                size={
                                                    20
                                                }
                                                thickness={
                                                    5
                                                }
                                                sx={{
                                                    color:
                                                        COLORS.white,
                                                }}
                                            />

                                            Sending
                                            Code...
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                gap:
                                                    .8,
                                            }}
                                        >
                                            Send Verification Code

                                            <ArrowForward
                                                sx={{
                                                    fontSize:
                                                        18,
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Button>

                                <Button
                                    fullWidth
                                    startIcon={
                                        <ArrowBack />
                                    }
                                    onClick={() =>
                                        navigate(
                                            "/login"
                                        )
                                    }
                                    sx={{
                                        mt: 1.3,
                                        minHeight:
                                            48,
                                        color:
                                            COLORS.muted,
                                        fontSize:
                                            ".78rem",
                                        fontWeight:
                                            800,
                                        textTransform:
                                            "none",
                                        borderRadius:
                                            "10px",
                                        "&:hover":
                                            {
                                                background:
                                                    COLORS.paleGreen,
                                                color:
                                                    COLORS.navy,
                                            },
                                    }}
                                >
                                    Back to Login
                                </Button>
                            </Box>

                            {/* MESSAGE */}

                            {message && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 1.5,
                                        borderRadius:
                                            "10px",
                                        background:
                                            message
                                                .toLowerCase()
                                                .includes(
                                                    "sent"
                                                )
                                                ? COLORS.paleGreen
                                                : "#FEF3F2",
                                        border:
                                            message
                                                .toLowerCase()
                                                .includes(
                                                    "sent"
                                                )
                                                ? "1px solid rgba(22,131,75,.14)"
                                                : "1px solid rgba(180,35,24,.14)",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            textAlign:
                                                "center",
                                            fontSize:
                                                ".75rem",
                                            fontWeight:
                                                700,
                                            lineHeight:
                                                1.5,
                                            color:
                                                message
                                                    .toLowerCase()
                                                    .includes(
                                                        "sent"
                                                    )
                                                    ? COLORS.green
                                                    : COLORS.red,
                                        }}
                                    >
                                        {message}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* FOOTER */}

                        <Box
                            sx={{
                                mt: 2.5,
                                textAlign:
                                    "center",
                            }}
                        >
                            <Box
                                sx={{
                                    display:
                                        "flex",
                                    justifyContent:
                                        "center",
                                    alignItems:
                                        "center",
                                    gap: .7,
                                    mb: .7,
                                }}
                            >
                                <Engineering
                                    sx={{
                                        fontSize:
                                            14,
                                        color:
                                            COLORS.green,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontSize:
                                            ".61rem",
                                        fontWeight:
                                            900,
                                        letterSpacing:
                                            .7,
                                        color:
                                            COLORS.green,
                                    }}
                                >
                                    PROFESSIONAL SKILLS • PRACTICAL TRAINING
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize:
                                        ".62rem",
                                    color:
                                        COLORS.muted,
                                }}
                            >
                                Your account security
                                matters to us.
                            </Typography>
                        </Box>
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
};

export default ForgotPassword;

